import { AfterViewChecked, Component, ElementRef, IterableDiffers, OnInit, Input, ChangeDetectionStrategy, ViewChild, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { ListViewColumnFactory } from '../../../service/factory/list-view-column-factory.service';
import { ListViewBase } from '../list-view.base';
import { DefaultPageSize, PagingEvent } from '../paging/paging.component';
import { LanguageService } from '../../../service/language.service';
import { NavigationService } from '../../../service/navigation.service';
import { UserPreferencesService } from '../../../service/user-preferences.service';
import { getListPreferencesId } from '../../../helper/list-preferences-key';

@Component({
    selector: 'app-list-view',
    templateUrl: './list-view.component.html',
    styleUrls: ['./list-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ListViewComponent<TSource = any> extends ListViewBase<TSource> implements OnInit, AfterViewChecked {
  constructor(
    columnFactory: ListViewColumnFactory,
    languageService: LanguageService,
    iterableDiffers: IterableDiffers,
    private readonly navigation: NavigationService,
    private readonly userPreferences: UserPreferencesService
  ) {
    super(columnFactory, languageService, iterableDiffers);
  }

  @Input() isToolbarSticky: boolean = false;
  @Input() preferencesKey: string;
  @Input() pagingEnabled: boolean = true;
  @Input() rememberPagination = false;
  @Input() pageSizeOptions: number[];
  @Input() set pageSize(value: number) {
    if (!Number.isInteger(value) || value < 1) {
      return;
    }

    this.configuredPageSize = value;
    this.from = 0;
    this.to = value;
  }

  //This receives a randomized ID to scope commands/menus per list component instance (e.g., when multiple list components are on a page) so only the intended instance handles dispatched actions. - Atlan
  @Input() contextId?: string;

  from: number = 0;
  to: number = DefaultPageSize;
  private configuredPageSize: number = DefaultPageSize;
  private preferencesId?: number;
  private readonly paginationSubscriptions = new Subscription();

  get pageSize(): number {
    return this.configuredPageSize;
  }
  @ViewChild('scrollView') scrollView: ElementRef<HTMLDivElement>;
  @ViewChild('toolbarScrollView') toolbarScrollView: ElementRef<HTMLDivElement>;
  @ViewChild('toolbarScrollContent') toolbarScrollContent: ElementRef<HTMLDivElement>;
  @ViewChild('tableView') tableView: ElementRef<HTMLTableElement>;
  private scrollSyncInProgress = false;
  private toolbarScrollContentWidth = 0;

  ngOnInit() {
    if (!this.rememberPagination) return;

    const requestedPage = Number(this.navigation.getParameterValue('page'));
    const page = Number.isSafeInteger(requestedPage) && requestedPage > 1 && requestedPage <= 1000000 ? requestedPage : 1;
    if (!this.preferencesKey) {
      this.showPage(page);
      return;
    }

    this.preferencesId = getListPreferencesId(this.preferencesKey, this.navigation.url);
    this.paginationSubscriptions.add(
      this.userPreferences.getListPreferences(this.preferencesId).subscribe({
        next: preferences => {
          if (Number.isInteger(preferences.pageSize) && preferences.pageSize > 0) {
            this.configuredPageSize = preferences.pageSize;
          }
          this.showPage(page);
        },
        error: () => this.showPage(page),
      })
    );
  }

  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
  }

  protected override resetRows(): void {
    super.resetRows();
    if (this.rememberPagination) this.clampPage();
  }

  override ngOnDestroy(): void {
    this.paginationSubscriptions.unsubscribe();
    super.ngOnDestroy();
  }

  get currentPageNumber(): number {
    return Math.floor(this.from / this.pageSize) + 1;
  }

  private showPage(page: number): void {
    this.from = (page - 1) * this.pageSize;
    this.to = this.from + this.pageSize;
    this.clampPage();
  }

  private clampPage(): void {
    if (!this.itemsSource) return;
    const lastPage = Math.max(1, Math.ceil(this.itemsSource.length / this.pageSize));
    if (this.currentPageNumber > lastPage) {
      this.from = (lastPage - 1) * this.pageSize;
      this.navigation.setListPage(lastPage);
    }
    this.to = this.from + this.pageSize;
  }

  ngAfterViewChecked(): void {
    this.updateToolbarScrollWidth();
  }

  getRowNumber(index: number): number {
    return this.from + index + 1;
  }

  pagingChanged(e: PagingEvent) {
    const pageSizeChanged = e.itemsPerPage !== this.pageSize;
    if (pageSizeChanged) this.configuredPageSize = e.itemsPerPage;
    this.from = e.from;
    this.to = e.to;
    if (this.rememberPagination) {
      this.navigation.setListPage(e.pageNumber);
      if (pageSizeChanged && this.preferencesId !== undefined) {
        this.paginationSubscriptions.add(this.userPreferences.setListPageSize(this.preferencesId, e.itemsPerPage).subscribe());
      }
    }
  }

  get desktopRows() {
    return this.pagingEnabled ? this.rows.slice(this.from, this.to) : this.rows;
  }

  onToolbarScroll(): void {
    if (this.scrollSyncInProgress || !this.scrollView || !this.toolbarScrollView) return;

    this.scrollSyncInProgress = true;
    this.scrollView.nativeElement.scrollLeft = this.toolbarScrollView.nativeElement.scrollLeft;
    this.scrollSyncInProgress = false;
  }

  onTableScroll(): void {
    if (this.scrollSyncInProgress || !this.scrollView || !this.toolbarScrollView) return;

    this.scrollSyncInProgress = true;
    this.toolbarScrollView.nativeElement.scrollLeft = this.scrollView.nativeElement.scrollLeft;
    this.scrollSyncInProgress = false;
  }

  private updateToolbarScrollWidth(): void {
    if (!this.tableView || !this.toolbarScrollContent) {
      this.toolbarScrollContentWidth = 0;
      return;
    }

    const scrollContainer = this.scrollView?.nativeElement;
    const toolbarScroll = this.toolbarScrollView?.nativeElement;
    if (!scrollContainer || !toolbarScroll) return;

    const width = toolbarScroll.clientWidth + Math.max(scrollContainer.scrollWidth - scrollContainer.clientWidth, 0);
    const styleWidth = `${width}px`;
    if (width === this.toolbarScrollContentWidth && this.toolbarScrollContent.nativeElement.style.width === styleWidth) return;

    this.toolbarScrollContentWidth = width;
    this.toolbarScrollContent.nativeElement.style.width = styleWidth;

    if (toolbarScroll.scrollLeft !== scrollContainer.scrollLeft) {
      toolbarScroll.scrollLeft = scrollContainer.scrollLeft;
    }
  }
}
